/**
 * FormatToolbar — shared rich-text format bar for the editor.
 *
 * A custom COMPACT toolbar (see CompactToolbar) built directly on
 * lexical-multi's format hooks (useFormatText/Block/Element) — not the wide
 * default ToolbarPlugin. It acts on the currently-focused text widget: each
 * widget's Editor registers with MultiEditorProvider and calls
 * setCurrentEditor(id) on FOCUS_COMMAND (MultiEditorStorePlugin), so one shared
 * toolbar formats whichever block you're editing. No per-widget toolbar.
 *
 * Presentation:
 *   desktop — a FLOATING, draggable panel (like the Add palette), not a bar
 *     welded to the top; drag it by its ⠿ grip and it stays out of the way.
 *   mobile  — a fixed BAR docked directly on top of the on-screen keyboard
 *     (iOS-Notes-accessory style), tracked via visualViewport.
 *
 * Web-only: relies on document focus tracking + visualViewport + pointer
 * dragging. Native rich text has its own surface.
 *
 * Visibility tracks focus, not currentEditor: MultiEditorProvider never
 * clears currentEditor on blur, so we show while a contenteditable (or the
 * toolbar itself) holds focus and hide when focus leaves the editor.
 */
import { Fragment, type ReactNode, useEffect, useState } from "react";
import { FORMAT_TEXT_COMMAND } from "lexical";
import { html, css } from "react-strict-dom";
import { colors } from "@duro-app/tokens/tokens/colors.css";
import {
  microSpacing,
  radii,
  spacing,
} from "@duro-app/tokens/tokens/spacing.css";
import { sizes } from "@duro-app/tokens/tokens/sizes.css";
import { borders } from "@duro-app/tokens/tokens/borders.css";
import { shadows } from "@duro-app/tokens/tokens/shadows.css";
import { typography } from "@duro-app/tokens/tokens/typography.css";
import { duration } from "@duro-app/tokens/tokens/motion.css";
import { useIsMobile } from "./useIsMobile.js";
import {
  useEditors,
  useFormatText,
  useFormatBlock,
  useFormatElement,
} from "@fredericrous/lexical-multi";
import { useDockablePanel } from "./DockablePanel.js";
import { DockDropPreview } from "./DockDropPreview.js";
import { layers } from "@duro-app/tokens/tokens/layers.css";
import "./formatToolbar.css";

/** Selector for lexical-multi's dropdown menus (align / block-format /
 *  color). Each carries a `data-element-src="ui/DropDown.tsx:*"` marker
 *  baked into the published dist. The popover portals to <body> (the
 *  trigger button stays in the toolbar), so a matching element that is
 *  NOT inside the toolbar container is an OPEN menu. */
const DROPDOWN_SEL = '[data-element-src^="ui/DropDown.tsx"]';
// react-strict-dom has no className, so the toolbar containers are tagged with
// data-testid (RSD's sanctioned custom attribute) and matched on that instead.
const TOOLBAR_SEL =
  '[data-testid="wb-format-dock"], [data-testid="wb-format-sheet"]';

/**
 * Keep the text editor focused when the toolbar is clicked — otherwise pressing
 * a button (or grabbing the drag grip, or clicking the panel's padding) blurs
 * the contenteditable, and the focus-tracked visibility hides the very bar you
 * just clicked (that's the "it disappears when I click it" bug). preventDefault
 * on mousedown stops the focus shift without blocking the click. Real inputs
 * (the font-size field) are exempted so they can still be focused/typed — the
 * `inToolbar` check keeps the bar visible while one of them holds focus.
 */
export function keepEditorFocus(e: React.MouseEvent) {
  const t = e.target as HTMLElement;
  if (!t.closest('input, textarea, [contenteditable="true"]')) {
    e.preventDefault();
  }
}

/** True while a text block (or the toolbar) holds focus. Seeds from the current
 *  focus so a fresh mount mid-edit (e.g. the docked group re-mounting the format
 *  body when you switch back from the Add tab) starts enabled rather than
 *  greyed — there's no new focusin to flip it, since focus never left. */
function useEditingText(): boolean {
  const [editing, setEditing] = useState(
    () =>
      typeof document !== "undefined" &&
      !!(document.activeElement as HTMLElement | null)?.closest?.(
        '[contenteditable="true"]',
      ),
  );
  useEffect(() => {
    const onIn = (e: FocusEvent) => {
      const t = e.target as HTMLElement | null;
      if (t?.closest('[contenteditable="true"]')) setEditing(true);
    };
    const onOut = () => {
      // After focus settles, hide unless focus is still in an editor or the
      // toolbar (toolbar clicks must not dismiss it).
      setTimeout(() => {
        const a = document.activeElement as HTMLElement | null;
        const inEditor = a?.closest('[contenteditable="true"]');
        const inToolbar = a?.closest(TOOLBAR_SEL);
        // A lexical-multi dropdown menu (align/block/color) portals to <body>
        // and steals focus when opened, which would otherwise hide the bar that
        // owns it — keep the bar while a menu is focused, OR while any menu
        // popover is open (rendered only while expanded; a popover OUTSIDE the
        // toolbar = open menu).
        const inDropdown = a?.closest(DROPDOWN_SEL);
        const menuOpen = Array.from(
          document.querySelectorAll(DROPDOWN_SEL),
        ).some((d) => !d.closest(TOOLBAR_SEL));
        if (!inEditor && !inToolbar && !inDropdown && !menuOpen) {
          setEditing(false);
        }
      }, 0);
    };
    document.addEventListener("focusin", onIn);
    document.addEventListener("focusout", onOut);
    return () => {
      document.removeEventListener("focusin", onIn);
      document.removeEventListener("focusout", onOut);
    };
  }, []);
  return editing;
}

/**
 * Height of the on-screen virtual keyboard (0 when hidden) via visualViewport,
 * so the mobile format bar can sit right on top of it — the iOS-Notes accessory
 * behaviour. Ported from mvp-first's design-system useKeyboardVisibility: the
 * visualViewport resize often fires before the keyboard is fully up, so a
 * debounced touchend re-check catches the settled height.
 */
function useKeyboardHeight(): number {
  const [h, setH] = useState(0);
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    let raf = 0;
    let deb = 0;
    const update = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        setH(Math.max(0, window.innerHeight - vv.height - vv.offsetTop));
      });
    };
    const onTouchEnd = () => {
      clearTimeout(deb);
      deb = window.setTimeout(update, 200);
    };
    vv.addEventListener("resize", update);
    vv.addEventListener("scroll", update);
    window.addEventListener("touchend", onTouchEnd);
    update();
    return () => {
      vv.removeEventListener("resize", update);
      vv.removeEventListener("scroll", update);
      window.removeEventListener("touchend", onTouchEnd);
      cancelAnimationFrame(raf);
      clearTimeout(deb);
    };
  }, []);
  return h;
}

// ── compact toolbar ────────────────────────────────────────────────────────

// Raw <svg> (RSD has no html.svg) — icon CONTENT passed as children of the RSD
// button, web-only. width/height are set here since the CSS class that sized
// them is gone (styles moved to css.create).
const svg = (path: ReactNode) => (
  <svg
    viewBox="0 0 24 24"
    width={16}
    height={16}
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ display: "block" }}
    aria-hidden
  >
    {path}
  </svg>
);
const ICON = {
  bullet: svg(
    <>
      <circle cx="5" cy="7" r="1.4" fill="currentColor" stroke="none" />
      <circle cx="5" cy="12" r="1.4" fill="currentColor" stroke="none" />
      <circle cx="5" cy="17" r="1.4" fill="currentColor" stroke="none" />
      <path d="M10 7h9M10 12h9M10 17h9" />
    </>,
  ),
  link: svg(
    <>
      <path d="M10 13a5 5 0 0 0 7 0l2-2a5 5 0 0 0-7-7l-1 1" />
      <path d="M14 11a5 5 0 0 0-7 0l-2 2a5 5 0 0 0 7 7l1-1" />
    </>,
  ),
  code: svg(<path d="M9 8l-4 4 4 4M15 8l4 4-4 4" />),
  left: svg(<path d="M4 6h16M4 12h11M4 18h14" />),
  center: svg(<path d="M4 6h16M7 12h10M5 18h14" />),
};

// Styles for the toolbar + floating panel. react-strict-dom needs longhand
// properties, pseudo-states via nested objects, and conditional/dynamic values
// via style arrays + parameterized functions. The button's active ("on") state
// used to be a `[data-on]` CSS selector; RSD forbids arbitrary data-*, so it's
// a conditional style instead.
const s = css.create({
  btn: {
    width: sizes.iconButton,
    height: sizes.iconButton,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderStyle: "none",
    padding: 0,
    backgroundColor: { default: "transparent", ":hover": colors.bgCard },
    color: colors.text,
    borderRadius: radii.px6,
    fontSize: typography.fontSizeSm,
    lineHeight: 1,
    cursor: "pointer",
    transitionProperty: "background-color, color",
    transitionDuration: duration.quick,
    outlineStyle: "solid",
    outlineColor: colors.accent,
    outlineWidth: { default: 0, ":focus-visible": borders.focusRing },
    outlineOffset: borders.focusOffsetSm,
  },
  btnOn: {
    backgroundColor: { default: colors.infoBg, ":hover": colors.infoBg },
    color: colors.accent,
  },
  // Greyed / inert while no text block is being edited (the panel stays put like
  // an Illustrator inspector; controls light up when you enter a text block).
  btnDisabled: {
    color: colors.border,
    cursor: "default",
    backgroundColor: { default: "transparent", ":hover": "transparent" },
  },
  label: {
    display: "block",
    marginTop: microSpacing.px2,
    marginRight: microSpacing.px2,
    marginBottom: spacing.xs,
    marginLeft: microSpacing.px2,
    fontSize: "9.5px",
    fontWeight: typography.fontWeightBold,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: colors.textMuted,
  },
  vsep: {
    width: sizes.divider,
    alignSelf: "stretch",
    backgroundColor: colors.border,
    marginTop: spacing.xs,
    marginBottom: spacing.xs,
    marginLeft: spacing.xs,
    marginRight: spacing.xs,
    flexShrink: 0,
  },
  bar: {
    display: "flex",
    alignItems: "center",
    gap: microSpacing.px1,
    paddingTop: spacing.xs,
    paddingBottom: spacing.xs,
    paddingLeft: microSpacing.px6,
    paddingRight: microSpacing.px6,
  },
  panelWrap: {
    paddingTop: spacing.xs,
    paddingRight: spacing.xs,
    paddingBottom: spacing.xs,
    paddingLeft: spacing.xs,
  },
  groupWrap: {
    paddingTop: spacing.xs,
    paddingRight: spacing.sm,
    paddingBottom: microSpacing.px6,
    paddingLeft: spacing.sm,
  },
  btnGrid: {
    display: "grid",
    gridTemplateColumns: `repeat(4, ${sizes.iconButton})`,
    gap: microSpacing.px2,
  },
  bodyScroll: { overflowX: "auto" },
  // ── standalone format panel (dock) ──
  dock: {
    position: "fixed",
    display: "flex",
    flexDirection: "column",
    maxWidth: "min(96vw, 760px)",
    backgroundColor: colors.bg,
    borderWidth: borders.hairline,
    borderStyle: "solid",
    borderColor: colors.border,
    borderRadius: radii.sm,
  },
  dockPos: (left: number, top: number) => ({ left, top }),
  zHi: { zIndex: layers.modal },
  zLo: { zIndex: layers.overlay },
  shadowIdle: { boxShadow: shadows.md },
  shadowDock: {
    boxShadow: shadows.dropOver,
  },
  // Fused header: one draggable row = grip cue + title + collapse chevron.
  header: {
    width: "100%",
    paddingTop: "0.45rem",
    paddingBottom: "0.45rem",
    paddingLeft: "0.6rem",
    paddingRight: "0.4rem",
    backgroundColor: colors.bgCard,
    display: "flex",
    alignItems: "center",
    gap: "0.45rem",
    cursor: "move",
    touchAction: "none",
    userSelect: "none",
    fontSize: "0.8rem",
    fontWeight: typography.fontWeightSemibold,
    color: colors.text,
  },
  headerBorder: {
    borderBottomWidth: borders.hairline,
    borderBottomStyle: "solid",
    borderBottomColor: colors.border,
  },
  grip: { color: colors.border, fontSize: "0.7rem", letterSpacing: "0.1em" },
  chevBtn: {
    marginLeft: "auto",
    borderStyle: "none",
    backgroundColor: { default: "transparent", ":hover": colors.bgCardHover },
    cursor: "pointer",
    color: colors.textMuted,
    fontSize: "0.8rem",
    lineHeight: 1,
    paddingTop: microSpacing.px3,
    paddingBottom: microSpacing.px3,
    paddingLeft: microSpacing.px6,
    paddingRight: microSpacing.px6,
    borderRadius: radii.px6,
  },
  // ── mobile keyboard bar (sheet) ──
  sheet: {
    position: "fixed",
    left: 0,
    right: 0,
    zIndex: layers.modalRaised, // above the "+ Add" bottom bar (layers.modal)
    backgroundColor: colors.bg,
    borderTopWidth: borders.hairline,
    borderTopStyle: "solid",
    borderTopColor: colors.border,
    boxShadow: shadows.sm,
    overflowX: "auto",
  },
  sheetPos: (bottom: number) => ({ bottom }),
  sheetShow: { display: "flex" },
  sheetHide: { display: "none" },
});

// Glyph styles for the text-format buttons (were inline styles on raw tags).
const glyph = css.create({
  bold: { fontWeight: 800 },
  italic: { fontFamily: "Georgia, serif", fontStyle: "italic" },
  underline: { textDecorationLine: "underline", textUnderlineOffset: 2 },
  strike: { textDecorationLine: "line-through" },
  heading: {
    fontWeight: typography.fontWeightBold,
    fontSize: typography.fontSizeXs,
  },
});

interface Tool {
  key: string;
  title: string;
  on: boolean;
  press: () => void;
  glyph: ReactNode;
}

function Btn({ tool, disabled }: { tool: Tool; disabled: boolean }) {
  return (
    <html.button
      type="button"
      aria-label={tool.title}
      aria-pressed={tool.on}
      disabled={disabled}
      onClick={tool.press}
      style={[
        s.btn,
        !disabled && tool.on && s.btnOn,
        disabled && s.btnDisabled,
      ]}
    >
      {tool.glyph}
    </html.button>
  );
}

/**
 * Custom compact format toolbar (Illustrator-panel style) built directly on
 * lexical-multi's format hooks — not the wide default ToolbarPlugin. Renders as
 * a labelled 4-per-row grid in the desktop panel, and as a single horizontal
 * scroll strip in the mobile keyboard bar.
 */
function CompactToolbar({
  variant,
  active,
}: {
  variant: "panel" | "bar";
  active: boolean;
}) {
  const text = useFormatText();
  const block = useFormatBlock();
  const elem = useFormatElement();
  const { currentEditor } = useEditors();

  const groups: { label: string; tools: Tool[] }[] = [
    {
      label: "Style",
      tools: [
        {
          key: "b",
          title: "Bold",
          on: text.isBold,
          press: () => text.dispatchFormatText("bold"),
          glyph: <html.b style={glyph.bold}>B</html.b>,
        },
        {
          key: "i",
          title: "Italic",
          on: text.isItalic,
          press: () => text.dispatchFormatText("italic"),
          glyph: <html.i style={glyph.italic}>I</html.i>,
        },
        {
          key: "u",
          title: "Underline",
          on: text.isUnderline,
          press: () => text.dispatchFormatText("underline"),
          glyph: <html.span style={glyph.underline}>U</html.span>,
        },
        {
          key: "s",
          title: "Strikethrough",
          on: text.isStrikethrough,
          // dispatchFormatText's type excludes strikethrough; go direct.
          press: () =>
            currentEditor?.dispatchCommand(
              FORMAT_TEXT_COMMAND,
              "strikethrough",
            ),
          glyph: <html.span style={glyph.strike}>S</html.span>,
        },
      ],
    },
    {
      label: "Block",
      tools: [
        {
          key: "h1",
          title: "Heading 1",
          on: block.blockType === "h1",
          press: () => block.dispatchBlockFormat("h1"),
          glyph: <html.span style={glyph.heading}>H1</html.span>,
        },
        {
          key: "h2",
          title: "Heading 2",
          on: block.blockType === "h2",
          press: () => block.dispatchBlockFormat("h2"),
          glyph: <html.span style={glyph.heading}>H2</html.span>,
        },
        {
          key: "quote",
          title: "Quote",
          on: block.blockType === "quote",
          press: () => block.dispatchBlockFormat("quote"),
          glyph: "“",
        },
        {
          key: "bullet",
          title: "Bulleted list",
          on: block.blockType === "bullet",
          press: () => block.dispatchBlockFormat("bullet"),
          glyph: ICON.bullet,
        },
      ],
    },
    {
      label: "Align & insert",
      tools: [
        {
          key: "left",
          title: "Align left",
          on: elem.elementFormat === "left" || elem.elementFormat === "",
          press: () => elem.dispatchElementFormat("left"),
          glyph: ICON.left,
        },
        {
          key: "center",
          title: "Align center",
          on: elem.elementFormat === "center",
          press: () => elem.dispatchElementFormat("center"),
          glyph: ICON.center,
        },
        {
          key: "link",
          title: "Link",
          on: elem.isLink,
          press: () => elem.insertLink(),
          glyph: ICON.link,
        },
        {
          key: "code",
          title: "Inline code",
          on: text.isCode,
          press: () => text.dispatchFormatText("code"),
          glyph: ICON.code,
        },
      ],
    },
  ];

  if (variant === "bar") {
    // Mobile: one horizontal scroll strip, hairline between groups.
    return (
      <html.div style={s.bar}>
        {groups.map((g, gi) => (
          <Fragment key={g.label}>
            {gi > 0 && <html.span style={s.vsep} />}
            {g.tools.map((t) => (
              <Btn key={t.key} tool={t} disabled={!active} />
            ))}
          </Fragment>
        ))}
      </html.div>
    );
  }

  // Desktop: labelled 4-per-row grid.
  return (
    <html.div style={s.panelWrap}>
      {groups.map((g) => (
        <html.div key={g.label} style={s.groupWrap}>
          <html.span style={s.label}>{g.label}</html.span>
          <html.div style={s.btnGrid}>
            {g.tools.map((t) => (
              <Btn key={t.key} tool={t} disabled={!active} />
            ))}
          </html.div>
        </html.div>
      ))}
    </html.div>
  );
}

/**
 * The format panel's inner body (grip + compact toolbar), reused by BOTH the
 * standalone DesktopFormatPanel and the docked group (PanelDockGroup) so the
 * controls are identical either way. Computes its own "editing" state so the
 * group can show it enabled/greyed without threading props across the store.
 */
export function FormatPanelBody() {
  const { currentEditor } = useEditors();
  const editing = useEditingText();
  const show = editing && currentEditor != null;
  return (
    <html.div style={s.bodyScroll} onMouseDown={keepEditorFocus}>
      <CompactToolbar variant="panel" active={show} />
    </html.div>
  );
}

/** Desktop: a floating, draggable format panel (same affordance as the Add
 *  palette). Hidden while docked into the tabbed group (PanelDockGroup renders
 *  the format body then). Registers its client rect so the Add palette can
 *  hit-test a drag against it, and docks when dragged onto the Add palette. */
function DesktopFormatPanel({ show }: { show: boolean }) {
  // Seed at the TOP-RIGHT, mirroring the Add palette's top-left — a stable,
  // predictable home instead of the old center-top seed that read as
  // "anarchic" once dragged/torn. ~360px is the compact panel's typical width.
  const initial =
    typeof window !== "undefined"
      ? { x: Math.max(16, window.innerWidth - 376), y: 56 }
      : { x: 16, y: 56 };
  // Dock behaviour (drag, hit-test, tear-off, ungroup, z-order) is shared with
  // Add / Media / Layout; only the RSD/StyleX markup below is this panel's own.
  const { grouped, docking, atFront, ref, pos, onHandleDown, bringToFront } =
    useDockablePanel("format", initial);
  // Collapsible like the Add palette (universal collapse) — expanded by default.
  const [open, setOpen] = useState(true);

  if (grouped) return null;
  // CONTEXTUAL: the formatting controls are inert unless a text block is being
  // edited, so the panel only exists then (progressive disclosure — Framer/
  // Canva). This is what removes the "always-there, drifting" feel: no text
  // edit, no panel; a clean canvas at rest. Mobile has its own keyboard bar.
  if (!show) return null;

  return (
    <html.div
      ref={ref}
      data-testid="wb-format-dock"
      onMouseDown={keepEditorFocus}
      onPointerDown={bringToFront}
      // Always visible (like an Illustrator inspector), SOLID — only the controls
      // grey out when no text block is being edited (fading the whole panel's
      // opacity made it read as transparent). Selected panel sits above the
      // other (front); raised further while dragged onto the other to dock.
      style={[
        s.dock,
        s.dockPos(pos.x, pos.y),
        atFront ? s.zHi : s.zLo,
        docking ? s.shadowDock : s.shadowIdle,
      ]}
    >
      {/* One fused header: DRAG the panel by this row (grip cue + title), and
          COLLAPSE via the chevron button (it stops the pointer so pressing it
          doesn't start a drag) — mirrors the Add palette. While another panel is
          dragged here, the drop-preview stands in (so "Format" isn't doubled). */}
      {docking ? (
        <DockDropPreview me="format" />
      ) : (
        <html.div
          onPointerDown={onHandleDown}
          style={[s.header, open && s.headerBorder]}
        >
          <html.span aria-hidden style={s.grip}>
            ⠿
          </html.span>
          Format
          <html.button
            type="button"
            onClick={() => setOpen((v) => !v)}
            onPointerDown={(e: React.PointerEvent) => e.stopPropagation()}
            aria-expanded={open}
            aria-label={open ? "Collapse Format" : "Expand Format"}
            style={s.chevBtn}
          >
            {open ? "▾" : "▸"}
          </html.button>
        </html.div>
      )}
      {open && (
        <html.div style={s.bodyScroll}>
          <CompactToolbar variant="panel" active={show} />
        </html.div>
      )}
    </html.div>
  );
}

/** Mobile: a fixed bar docked directly above the on-screen keyboard. */
function MobileFormatBar({ show }: { show: boolean }) {
  const keyboard = useKeyboardHeight();
  return (
    <html.div
      data-testid="wb-format-sheet"
      onMouseDown={keepEditorFocus}
      // sit right on top of the keyboard (iOS Notes accessory)
      style={[s.sheet, s.sheetPos(keyboard), show ? s.sheetShow : s.sheetHide]}
    >
      <CompactToolbar variant="bar" active={show} />
    </html.div>
  );
}

export function FormatToolbar() {
  const { currentEditor } = useEditors();
  const isMobile = useIsMobile();
  const editing = useEditingText();
  const show = editing && currentEditor != null;

  return isMobile ? (
    <MobileFormatBar show={show} />
  ) : (
    <DesktopFormatPanel show={show} />
  );
}
